import React from 'react';
import strings from 'strings';
import InfoLabelsGrid from 'UI/components/organisms/inventoryProfiles/InfoLabelsGrid';
import InventoryOverviewLayout from 'UI/components/organisms/inventoryProfiles/InventoryOverviewLayout';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { CANDIDATES_PROFILE_HASHES } from 'UI/constants/hashIds';
import { RequestStatus } from 'UI/constants/status';

import { useCandidateScrollToSection } from './CandidateOverview.hooks';
import { DefaultCards } from './components';
import {
  candidateBackgroundLabels,
  establishingUrgencyLabels,
  professionalInsights,
  workPreferencesLabels
} from './labels';

/**
 * @param {Object} props
 * @param {Object} [props.profile]
 * @param {boolean} props.loading
 * @param {Function} props.onWriteUpClick
 * @param {Function} props.onEditClick
 */
const CandidateOverview = ({
  isProfileLoading,
  profile,
  onWriteUpClick,
  candidateDataSheet,
  candidateDataSheetStatus,
  onEditClick
}) => {
  const loading =
    isProfileLoading ||
    [RequestStatus.Idle, RequestStatus.Loading].includes(candidateDataSheetStatus);
  const success = candidateDataSheetStatus === RequestStatus.Success;

  useCandidateScrollToSection({ success, loading });

  const createDataSheetCards = () => {
    return [
      {
        cardProps: {
          headerProps: {
            title: 'Establishing Urgency'
          }
        },
        id: CANDIDATES_PROFILE_HASHES.EstablishingUrgency,
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: establishingUrgencyLabels(candidateDataSheet?.data?.establishingUrgency)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Candidate Background'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: candidateBackgroundLabels(candidateDataSheet?.data?.candidateBackground)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Work Preferences'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: workPreferencesLabels(candidateDataSheet?.data?.workPreferences)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Professional Insights'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: professionalInsights(candidateDataSheet?.data?.professionalInsights)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Notes'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: [
            {
              title: 'Notes',
              description: candidateDataSheet?.data?.qualification?.notes,
              config: { sm: 9, md: 9, xs: 9, style: { whiteSpace: 'pre-line' } }
            }
          ]
        }
      }
    ];
  };

  const overviewContent = (function getOverviewContent() {
    if (loading) {
      return <InventoryOverviewLayout items={createDataSheetCards()} loading />;
    }

    if (candidateDataSheetStatus === RequestStatus.Error) {
      return <EmptyPlaceholder {...strings.shared.errors.serverError} />;
    }

    if (!candidateDataSheet) {
      return (
        <EmptyPlaceholder subtitle={strings.inventoryProfiles.sections.tabs.dataSheet.emptyState} />
      );
    }

    return <InventoryOverviewLayout items={createDataSheetCards()} loading={false} />;
  })();

  return (
    <>
      <DefaultCards
        profile={profile}
        loading={loading}
        onEditClick={onEditClick}
        onWriteUpClick={onWriteUpClick}
        candidateDataSheet={candidateDataSheet}
      />
      {overviewContent}
    </>
  );
};

CandidateOverview.defaultProps = {
  profile: {}
};

export default CandidateOverview;
